'use client';

import { getDictionary, Locales } from '@/app/[lang]/dictonaries';
import bgImage from '@/public/dashboard-bg.png';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/utils/supabase/client';
import { Suspense } from 'react';

function UnsubscribeContent({ params }: { params: { lang: Locales } }) {
  const searchParams = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const type = searchParams.get('type');

  const [dict, setDict] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<'loading' | 'invalidToken' | 'loaded'>(
    'loading'
  );

  const supabase = createClient();

  const typesToColumns: Record<string, string> = {
    'sub-renew': 'sub_renew',
    'pre-delivery': 'pre_delivery',
    'post-delivery': 'post_delivery',
    'non-essential': 'marketing_emails'
  };

  useEffect(() => {
    const loadDictionary = async () => {
      const dictionary = await getDictionary(params.lang);
      setDict(dictionary);
    };

    const updatePreferences = async () => {
      // Check if type exists and maps to a valid column name
      const columnName = type ? typesToColumns[type] : undefined;

      if (!columnName || typeof columnName !== 'string') {
        setStatus('invalidToken');
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        'manage-email-preferences',
        {
          body: {
            token,
            email,
            update: {
              [columnName]: false
            }
          }
        }
      );
      if (data) {
        console.log('Got preferences:', data);
        setStatus('loaded');
      } else {
        setStatus('invalidToken');
      }
    };

    loadDictionary();

    // Log the token when it's available
    if (token && email) {
      console.log('Got token:', token);
      console.log('Got email:', email);
      updatePreferences();
    } else {
      setStatus('invalidToken');
    }
  }, [params.lang, token]);

  return (
    <div
      className="relative flex flex-col items-center justify-center bg-black h-full w-full min-h-[100vh] py-20 px-8 bg-cover bg-center bg-fixed"
      style={{ backgroundImage: `url(${bgImage.src})` }}
    >
      <div className="absolute top-0 left-0 w-full h-full backdrop-blur-md"></div>
      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center py-28 px-8">
        <div className="w-full md:w-[900px] md:py-8 py-6 px-6 md:px-12 bg-black rounded-3xl min-h-[400px] flex flex-col justify-between">
          {status === 'loading' && (
            <>
              <div>
                <div className="w-full h-[40px] rounded-full shimmer mb-4"></div>
                <div className="w-1/2 h-[40px] rounded-full shimmer mb-4"></div>
                <div className="w-1/2 h-[40px] rounded-full shimmer mb-4"></div>
                <div className="w-1/2 h-[40px] rounded-full shimmer mb-4"></div>
              </div>
              <div className="w-full flex justify-end">
                <div className="w-1/3 h-[40px] rounded-full shimmer"></div>
              </div>
            </>
          )}
          {status === 'invalidToken' && (
            <>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  The URL token is invalid.
                </h1>
                <p className="text-lg text-white/50">
                  Please check the link you received.
                </p>
              </div>
              <p className="text-lg text-white/50">
                Need help updating your email preferences? Contact us at{' '}
                <a href="mailto:info@calori.fi">info@calori.fi</a>
              </p>
            </>
          )}
          {status === 'loaded' && (
            <>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  You have successfully unsubscribed from this type of emails.
                </h1>
                <p className="text-lg text-white/50">
                  You will no longer receive this type of email from us.
                </p>
              </div>
              <p className="text-lg text-white/50 mt-8">
                You can manage other types of emails at the{' '}
                <a
                  className="underline text-white"
                  href={`/emails/manage?token=${token}&email=${email}`}
                >
                  email management page
                </a>
                .
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ManagePage({ params }: { params: { lang: Locales } }) {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <UnsubscribeContent params={params} />
    </Suspense>
  );
}
